'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../../styles/global/tokens.css';
import '../../styles/sidenav/sidenav.css';
import AccountSelector from './AccountSelector.jsx';
import AccountSourceToggle from './AccountSourceToggle.jsx';
import NavItem from './NavItem.jsx';
import Tooltip from './Tooltip.jsx';
import { usePortal } from './portal.js';
import {
  DEFAULT_ACCOUNT_FILTERS,
  applyAccountSourceLabels,
  withDefaultSourceBadges,
} from './accountSources.js';
import {
  containsActiveItem,
  findNavItem,
  isMarkedActive,
  isNavigationTarget,
  resolveActiveItem,
  resolveCurrentPath,
} from './activeItem.js';

import { useOptionalConfig, useOptionalLocalization } from '../../lib/providers/ConfigProvider.jsx';
import { useOptionalTheme } from '../../lib/providers/ThemeProvider.jsx';
import { useDocumentTheme } from './useDocumentTheme.js';
import { DEFAULT_LOGO_ALT, DEFAULT_LOGO_MINI_SRC, DEFAULT_LOGO_SRC } from './defaultLogo.js';

const SIDENAV_STATES = ['open', 'collapsed'];

/** Lee el estado guardado con `persistKey` (o null). */
const readPersistedState = (key) => {
  if (!key || typeof window === 'undefined') return null;
  try {
    const value = window.localStorage.getItem(key);
    return SIDENAV_STATES.includes(value) ? value : null;
  } catch {
    return null;
  }
};

const writePersistedState = (key, value) => {
  if (!key || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* almacenamiento bloqueado: no se persiste */
  }
};

/** Número → px; cualquier otro valor CSS se respeta. */
const toCssLength = (value) => (typeof value === 'number' ? `${value}px` : value);

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Componente Sidenav principal con configuración dinámica
 * Soporta dos estados: open y collapsed
 * Puede funcionar con o sin ConfigProvider
 */
function Sidenav({ 
  // Props directas (prioritarias sobre el provider)
  items = null,
  logo = null,
  footerItems = null,
  theme = 'light',
  defaultState = 'open',
  initialState = null,
  // Estado controlado (con onStateChange). Sin él, el Sidenav lo maneja solo.
  state: controlledState = undefined,
  // Recuerda abierto/colapsado en localStorage (sólo sin `state` controlado).
  persistKey = null,
  // 'rail' (la tarjeta flotante de siempre) o 'drawer' (panel lateral con
  // fondo, para móvil). El drawer se controla con open / onOpenChange.
  variant = 'rail',
  open: drawerOpen = false,
  onOpenChange = null,
  // Anchos (número en px o cualquier longitud CSS). Por defecto los tokens.
  width = null,
  collapsedWidth = null,
  // Publica --sidenav-current-width en <html> para que la página se desplace
  // o centre sus modales respecto al espacio libre.
  publishWidthVar = false,
  
  // Configuración de comportamiento
  responsive = null,
  accessibility = null,
  // Escape (con el foco dentro del sidenav) lo colapsa.
  collapseOnEscape = true,
  showTooltips = true,
  animations = true,
  // Flecha de navegación en items hoja de nivel 0 (los que llevan a otra vista).
  // Opt-in: sin ella, un consumidor que actualice la librería no ve elementos
  // nuevos en su navegación.
  showNavigateIcons = false,
  // Ruta actual según el router del proyecto (usePathname, useLocation…).
  // Sin ella se lee window.location.pathname, que en routers que actualizan la
  // URL después de renderizar (Next.js App Router) deja resaltada la vista
  // anterior. Ver activeItem.js.
  currentPath = null,
  // Navegación con el router de la app: `onNavigate(item, event)` para ítems
  // sin onClick propio (p. ej. router.push(item.href)), o `linkComponent` para
  // renderizar los enlaces con el Link del router. Sin ninguno de los dos se
  // navega con window.location (comportamiento histórico).
  onNavigate = null,
  linkComponent = null,
  // Al hacer clic en una opción, se marca al instante como seleccionada y con
  // un indicador de carga hasta que cambie la ruta: sin esto, en rutas que
  // tardan en cargar parece que el clic no hizo nada. Se limpia al cambiar
  // `currentPath` o, si la navegación nunca llega, tras el timeout. El
  // indicador (no la selección) espera `navigationPendingDelayMs`: una
  // navegación rápida no lo llega a pintar.
  showNavigationPending = true,
  navigationPendingDelayMs = 300,
  navigationPendingTimeoutMs = 10000,
  
  // Callbacks
  onStateChange = null,

  // Selector de cuentas (opt-in mediante showAccountSelector)
  showAccountSelector = false,
  accounts = [],
  selectedAccountId = null,
  // Objeto de la cuenta seleccionada, para APIs paginadas donde puede no venir
  // en el lote actual (el trigger la sigue mostrando).
  selectedAccount = null,
  onAccountSelect = null,
  accountsLoading = false,
  accountsError = null,
  accountSearchable = true,
  // Chips de filtro. Con `showAccountFilters` y sin `accountFilters`, se usan
  // los estándar (All / GTS / GBS). Pasar `accountFilters` los reemplaza.
  showAccountFilters = false,
  accountFilters = null,
  selectedAccountFilterId = null,
  onAccountFilterChange = null,
  allAccountFilterId = 'all',
  // Búsqueda de cuentas: 'local' (default) filtra en cliente; 'remote' delega
  // al proyecto vía onAccountSearchChange (para APIs paginadas).
  accountSearchMode = 'local',
  onAccountSearchChange = null,
  accountSearchDebounceMs = 300,
  accountsFetching = false,
  accountsTotal = null,
  // Muestra el toggle de fuente debajo del selector. Solo aparece si la cuenta
  // seleccionada tiene 2+ fuentes: si solo tiene una, no hay nada que alternar.
  // Sin `accountSources`, el toggle usa las opciones estándar (All / GTS / GBS).
  // Mientras el toggle se ve, el trigger no repite las fuentes como badges.
  showAccountSourceToggle = false,
  accountSources = null,
  // Textos de las fuentes por id, p. ej. { tech: 'Tech' }. Se aplican a los
  // chips, al toggle y a los badges que genera la librería (`account.sources`).
  accountSourceLabels = null,
  selectedAccountSourceId = null,
  onAccountSourceChange = null,
  
  // Localización
  translations = null,
  locale = 'en',
  
  // Estilos
  className = '',
  style = {},
  
  ...props 
}) {
  const inPortal = usePortal();
  // Providers opcionales: el Sidenav funciona con o sin ellos.
  const configContext = useOptionalConfig();
  const localizationContext = useOptionalLocalization();
  const themeContext = useOptionalTheme();
  const navigationContext = configContext ? { items: configContext.navigation } : null;
  const logoContext = configContext?.logo ? { getCurrentLogo: () => configContext.logo } : null;

  // Función para obtener valores con fallbacks
  const getConfigValue = (path, defaultValue) => {
    if (configContext?.getConfigValue) {
      return configContext.getConfigValue(path, defaultValue);
    }
    return defaultValue;
  };
  
  // Función de traducción
  const t = (key, fallback) => {
    // 1. Usar traducciones pasadas por props
    if (translations && translations[key]) {
      return translations[key];
    }
    // 2. Usar contexto de localización si existe
    if (localizationContext?.t) {
      return localizationContext.t(key, fallback);
    }
    // 3. Fallback
    return fallback || key;
  };
  
  // Obtener items de navegación (props o provider)
  const navigationItems = items || navigationContext?.items || [];
  
  // Obtener configuración del logo (props o provider)
  const getCurrentLogo = () => {
    if (logo) return logo;
    if (logoContext?.getCurrentLogo) return logoContext.getCurrentLogo();
    return {};
  };
  
  // Obtener tema actual (props o provider)
  // theme="auto" sigue a la app: clase .dark o data-theme en <html>.
  const documentTheme = useDocumentTheme(theme === 'auto');
  const currentTheme =
    theme === 'auto' ? documentTheme : theme || themeContext?.currentTheme || 'light';
  
  // Estado del sidenav: controlado (`state`) o interno, opcionalmente
  // persistido con `persistKey`. En modo drawer siempre se muestra abierto.
  const isControlled = SIDENAV_STATES.includes(controlledState);
  const [internalState, setInternalState] = useState(
    () =>
      readPersistedState(persistKey) ||
      initialState ||
      defaultState ||
      getConfigValue('settings.defaultState', 'open')
  );
  const isDrawer = variant === 'drawer';
  const state = isDrawer ? 'open' : isControlled ? controlledState : internalState;
  const stateRef = useRef(state);
  stateRef.current = state;
  
  // Estado del panel del selector de cuentas
  const [accountPanelOpen, setAccountPanelOpen] = useState(false);

  /**
   * Fuentes que se ofrecen en el toggle.
   * Se limita a las que la cuenta seleccionada declara en `scopes`, más la
   * opción "todas" (`allAccountFilterId`). Con menos de dos no se renderiza.
   */
  // Memoizados: AccountSelector tiene efectos que dependen de `filters`, y una
  // lista nueva en cada render los volvería a disparar sin necesidad.
  const resolvedAccountFilters = useMemo(
    () =>
      applyAccountSourceLabels(
        Array.isArray(accountFilters)
          ? accountFilters
          : showAccountFilters
            ? DEFAULT_ACCOUNT_FILTERS
            : null,
        accountSourceLabels
      ),
    [accountFilters, showAccountFilters, accountSourceLabels]
  );

  const resolvedAccountSources = useMemo(
    () =>
      applyAccountSourceLabels(
        // "All" va incluido: con una cuenta GTS + GBS el toggle ofrece las dos
        // fuentes y la vista combinada.
        Array.isArray(accountSources) ? accountSources : DEFAULT_ACCOUNT_FILTERS,
        accountSourceLabels
      ),
    [accountSources, accountSourceLabels]
  );

  // Cuentas con `sources` y sin `badges` reciben los badges estándar.
  const resolvedAccounts = useMemo(
    () =>
      Array.isArray(accounts)
        ? accounts.map((a) => withDefaultSourceBadges(a, accountSourceLabels))
        : accounts,
    [accounts, accountSourceLabels]
  );
  const resolvedSelectedAccount = useMemo(
    () => withDefaultSourceBadges(selectedAccount, accountSourceLabels),
    [selectedAccount, accountSourceLabels]
  );

  const getAccountSourceOptions = () => {
    if (!showAccountSourceToggle) return null;

    const current =
      selectedAccount ||
      (Array.isArray(accounts)
        ? accounts.find((a) => a.id === selectedAccountId)
        : null);
    const scopes = Array.isArray(current?.scopes) ? current.scopes : [];

    const options = resolvedAccountSources.filter(
      (source) =>
        source.id === allAccountFilterId || scopes.includes(source.id)
    );

    // Las fuentes reales (sin contar "todas") deben ser 2+ para poder alternar
    const realSources = options.filter((o) => o.id !== allAccountFilterId);
    return realSources.length >= 2 ? options : null;
  };

  // Con el toggle visible las fuentes ya se ven como opciones, así que el
  // trigger deja de mostrarlas como badges. Una cuenta de una sola fuente no
  // tiene toggle y conserva su badge (GTS u GBS).
  const accountSourceOptions = getAccountSourceOptions();
  const accountSourceToggleVisible = state === 'open' && Boolean(accountSourceOptions);

  // Referencias
  const sidenavRef = useRef(null);
  const [tooltipTarget, setTooltipTarget] = useState(null);
  const [tooltipContent, setTooltipContent] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  
  /**
   * Un solo ítem activo por render: el más específico (ver activeItem.js).
   * Sus ancestros quedan como "has-active-child"; los hermanos, limpios.
   */
  const footerNavigationItems = footerItems || getConfigValue('footerItems', []);
  const navigationTrees = [navigationItems, footerNavigationItems];
  const resolvedPath = resolveCurrentPath(currentPath);

  // Navegación en curso: el ítem clicado y la ruta desde la que se salió.
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const pendingItem = pendingNavigation
    ? findNavItem(navigationTrees, pendingNavigation.item)
    : null;

  // Mientras se navega, el ítem clicado es el activo (optimista): el usuario ve
  // su selección al instante, con sus ancestros marcados como siempre.
  const activeItem =
    pendingItem || resolveActiveItem(navigationTrees, resolvedPath);

  // El indicador de carga solo se pinta si la navegación sigue pendiente pasado
  // `navigationPendingDelayMs`; si llega antes, no aparece y no hay parpadeo.
  // Se guarda la navegación para la que venció el plazo: una nueva (otro clic)
  // vuelve a esperar sin tener que reiniciar nada.
  const [indicatedNavigation, setIndicatedNavigation] = useState(null);
  const isPendingIndicatorVisible =
    pendingNavigation !== null &&
    (navigationPendingDelayMs <= 0 || indicatedNavigation === pendingNavigation);
  const indicatedPendingItem = isPendingIndicatorVisible ? pendingItem : null;

  const handleNavigateStart = (item) => {
    // En el drawer, elegir una opción que navega lo cierra (lo que cada app
    // hacía a mano con su Sheet).
    if (isDrawer && !(item.children && item.children.length) && (item.href || item.onClick)) {
      onOpenChange?.(false);
    }
    // La ruta de origen se lee en el momento del clic, no la del último render.
    const fromPath = resolveCurrentPath(currentPath);
    if (!showNavigationPending || !isNavigationTarget(item, fromPath)) return;
    setPendingNavigation({ item, fromPath, wasActive: isMarkedActive(item) });
  };

  // ¿Terminó la navegación? Se pregunta en el efecto y no con `resolvedPath`,
  // que es la ruta leída durante el render. Sin `currentPath` esa lectura es
  // `window.location`, y Next.js App Router cambia la URL en un
  // `useInsertionEffect`, DESPUÉS del render en el que el consumidor ya marcó
  // la vista nueva: ese render ve la ruta vieja, no limpia, y como no llega otro
  // render el indicador se quedaba puesto hasta el timeout. En el efecto la URL
  // ya es la nueva. También cuenta como llegada que el consumidor marque el
  // ítem clicado con `active` (su router ya sabe dónde está).
  const isNavigationSettled = (pending) => {
    if (resolveCurrentPath(currentPath) !== pending.fromPath) return true;
    if (pending.wasActive) return false;
    return isMarkedActive(findNavItem(navigationTrees, pending.item));
  };

  // Sin array de dependencias a propósito: los ítems llegan recreados en cada
  // render y la URL no es estado de React, así que se revisa en cada render
  // mientras haya una navegación pendiente (sin ella es un return inmediato).
  useEffect(() => {
    if (pendingNavigation && isNavigationSettled(pendingNavigation)) {
      setPendingNavigation(null);
    }
  });

  // Si el consumidor no pasa `currentPath` y no vuelve a renderizar el Sidenav
  // al navegar, ningún render llega a ver la URL nueva: se vigila la URL
  // mientras dure la navegación. Solo en ese caso y solo mientras hay una.
  useEffect(() => {
    if (!pendingNavigation || typeof currentPath === 'string') return undefined;
    if (typeof window === 'undefined') return undefined;
    const check = () => {
      if (window.location.pathname !== pendingNavigation.fromPath) {
        setPendingNavigation(null);
      }
    };
    const interval = setInterval(check, 100);
    window.addEventListener('popstate', check);
    return () => {
      clearInterval(interval);
      window.removeEventListener('popstate', check);
    };
  }, [pendingNavigation, currentPath]);

  useEffect(() => {
    if (!pendingNavigation || navigationPendingDelayMs <= 0) return undefined;
    const timer = setTimeout(
      () => setIndicatedNavigation(pendingNavigation),
      navigationPendingDelayMs
    );
    return () => clearTimeout(timer);
  }, [pendingNavigation, navigationPendingDelayMs]);

  // Red de seguridad: si la navegación nunca llega (el consumidor no navegó, o
  // su router la rechazó), no dejar el indicador puesto para siempre.
  useEffect(() => {
    if (!pendingNavigation) return undefined;
    const timer = setTimeout(
      () => setPendingNavigation(null),
      navigationPendingTimeoutMs
    );
    return () => clearTimeout(timer);
  }, [pendingNavigation, navigationPendingTimeoutMs]);

  const isItemActive = (item) => item === activeItem;

  const isItemOrChildActive = (item) => containsActiveItem(item, activeItem);

  // Configuración dinámica con valores por defecto
  const config = {
    responsive: responsive || getConfigValue('settings.responsive', { enabled: false }),
    accessibility: accessibility || getConfigValue('settings.accessibility', { enabled: true }),
    animations: animations !== undefined ? animations : getConfigValue('settings.animationDuration', 300),
    showTooltips: showTooltips !== undefined ? showTooltips : getConfigValue('settings.showTooltips', true)
  };

  /**
   * Cambiar estado del sidenav
   */
  const changeState = (newState) => {
    if (!SIDENAV_STATES.includes(newState) || newState === stateRef.current) return;
    if (!isControlled) setInternalState(newState);
    writePersistedState(persistKey, newState);
    onStateChange?.(newState);
  };

  /**
   * Alternar estado del sidenav
   */
  const toggleState = () => {
    if (isDrawer) {
      onOpenChange?.(false);
      return;
    }
    changeState(state === 'open' ? 'collapsed' : 'open');
  };

  /**
   * Manejar hover para tooltips
   */
  const handleItemHover = (event, item) => {
    if (state === 'collapsed' && config.showTooltips) {
      const rect = event.currentTarget.getBoundingClientRect();
      setTooltipTarget(event.currentTarget);
      setTooltipContent(t(item.label) || item.label);
      setTooltipPosition({
        x: rect.right + 8,
        y: rect.top + rect.height / 2
      });
    }
  };

  /**
   * Manejar salida del hover
   */
  const handleItemLeave = () => {
    setTooltipTarget(null);
    setTooltipContent('');
  };

  /**
   * Renderizar logo dinámico con soporte para URLs y componentes React
   */
  const renderLogo = () => {
    const logoConfig = getCurrentLogo();
    
    // Logo principal
    const imageSrc = logoConfig?.src || logoConfig?.image || DEFAULT_LOGO_SRC;
    
    // Logo mini para estado collapsed
    const miniLogoSrc = logoConfig?.miniSrc || logoConfig?.miniImage || logoConfig?.mini?.src || DEFAULT_LOGO_MINI_SRC;
    
    // Determinar qué logo mostrar según el estado
    const currentLogoSrc = state === 'collapsed' ? miniLogoSrc : imageSrc;

    // Tamaños por estado
    const sizeByState = {
      open: { height: 32, maxWidth: 180 },
      collapsed: { height: 32, maxWidth: 64 }
    };
    const { height, maxWidth } = sizeByState[state] || sizeByState.open;

    return (
      <div className={`sidenav-logo sidenav-logo--${state}`}>
        <img
          src={currentLogoSrc}
          alt={t('logo.alt', logoConfig?.alt || DEFAULT_LOGO_ALT)}
          className="sidenav-logo-image"
          style={{ height, maxWidth, width: 'auto', objectFit: 'contain' }}
        />
      </div>
    );
  };

  /**
   * Renderizar elementos de navegación
   */
  const renderNavItems = (items, level = 0, parentActive = false) => {
    if (!Array.isArray(items)) return null;

    return items.map((item, index) => {
      const hasActiveChild = isItemOrChildActive(item);
      const itemIsActive = isItemActive(item);
      
      return (
        <NavItem
          key={item.id}
          item={item}
          level={level}
          sidenavState={state}
          onHover={handleItemHover}
          onLeave={handleItemLeave}
          hasActiveChild={hasActiveChild}
          parentActive={parentActive}
          isLastChild={index === items.length - 1}
          showNavigateIcon={showNavigateIcons}
          activeItem={activeItem}
          pendingItem={indicatedPendingItem}
          onNavigateStart={handleNavigateStart}
          onNavigate={onNavigate}
          linkComponent={linkComponent}
        >
          {item.children && item.children.length > 0 && (
            <div className="sidenav-submenu">
              {renderNavItems(item.children, level + 1, itemIsActive || parentActive)}
            </div>
          )}
        </NavItem>
      );
    });
  };

  /**
   * Renderizar botón de toggle
   */
  const renderToggleButton = () => (
    <button
      className="sidenav-toggle"
      onClick={toggleState}
      aria-label={t('toggle.label', 'Toggle navigation')}
      title={t('toggle.title', 'Toggle navigation state')}
    >
      <svg 
        width="16" 
        height="16" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2"
        strokeLinecap="round" 
        strokeLinejoin="round"
      >
        {state === 'open' ? (
          <polyline points="15 18 9 12 15 6" />
        ) : (
          <polyline points="9 18 15 12 9 6" />
        )}
      </svg>
    </button>
  );
  
  /**
   * Renderizar elementos del footer
   */
  const renderFooterItems = () => {
    return renderNavItems(footerNavigationItems);
  };

  // Responsive: colapsa bajo el breakpoint móvil y abre por encima. Avisa con
  // onStateChange como cualquier otro cambio, y no se re-suscribe en cada
  // render (la config llega como objeto nuevo cada vez).
  const responsiveEnabled = Boolean(config.responsive?.enabled) && !isDrawer;
  const mobileBreakpoint = config.responsive?.breakpoints?.mobile || 768;
  useEffect(() => {
    if (!responsiveEnabled || typeof window === 'undefined') return undefined;

    const handleResize = () => {
      changeState(window.innerWidth <= mobileBreakpoint ? 'collapsed' : 'open');
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [responsiveEnabled, mobileBreakpoint]);

  // Teclado. Sólo actúa con el foco DENTRO del sidenav: antes escuchaba todo
  // el documento y un Escape en un modal de la app colapsaba el sidebar.
  const accessibilityEnabled = config.accessibility?.enabled !== false;
  useEffect(() => {
    if (!accessibilityEnabled || isDrawer) return undefined;

    const handleKeyDown = (event) => {
      const root = sidenavRef.current;
      if (!root || !root.contains(document.activeElement)) return;

      // Con el panel de cuentas abierto, Escape lo cierra él (AccountSelector)
      if (
        event.key === 'Escape' &&
        collapseOnEscape &&
        stateRef.current === 'open' &&
        !accountPanelOpen
      ) {
        changeState('collapsed');
      }

      if (event.key === 'Tab' && stateRef.current === 'collapsed') {
        // Mantener foco dentro del sidenav cuando está colapsado
        const focusableElements = root.querySelectorAll(FOCUSABLE);
        if (focusableElements.length > 0) {
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];
          if (event.shiftKey && document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          } else if (!event.shiftKey && document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [accessibilityEnabled, isDrawer, collapseOnEscape, accountPanelOpen]);

  // Drawer: foco atrapado, Escape cierra, scroll de la página bloqueado y el
  // foco vuelve a quien lo abrió.
  useEffect(() => {
    if (!isDrawer || !drawerOpen || typeof document === 'undefined') return undefined;

    const root = sidenavRef.current;
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusables = () => (root ? Array.from(root.querySelectorAll(FOCUSABLE)) : []);
    // El efecto que quita `inert` va declarado más abajo y corre DESPUÉS de
    // éste: sin quitarlo aquí, focus() sobre un hijo de un nodo inert no hace
    // nada y el foco se quedaba en el botón ☰, fuera del drawer y de la trampa.
    if (root) root.inert = false;
    const focusFirst = () => {
      if (root && root.contains(document.activeElement)) return true;
      const first = focusables()[0];
      if (first && typeof first.focus === 'function') first.focus();
      return Boolean(root && root.contains(document.activeElement));
    };
    // Los hijos con `transition: all` siguen heredando `visibility: hidden`
    // hasta que su transición arranca (uno o dos frames después de abrir), y
    // mientras tanto focus() no hace nada. Se reintenta frame a frame, con tope.
    let frame = null;
    let attempts = 0;
    const retry = () => {
      frame = null;
      if (focusFirst() || ++attempts >= 20) return;
      frame = requestAnimationFrame(retry);
    };
    if (!focusFirst() && typeof requestAnimationFrame === 'function') {
      frame = requestAnimationFrame(retry);
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (accountPanelOpen) return;
        event.stopPropagation();
        onOpenChange?.(false);
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) return;
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      // Foco fuera del drawer (p. ej. un clic en la página detrás del
      // backdrop): Tab lo devuelve dentro en vez de seguir por la página.
      if (!root || !root.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? lastItem : firstItem).focus();
        return;
      }
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault();
        firstItem.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [isDrawer, drawerOpen, accountPanelOpen]);

  // --sidenav-current-width en <html>: el espacio que ocupa el sidebar
  // (ancho + márgenes), o 0 en modo drawer. Opt-in.
  useEffect(() => {
    if (!publishWidthVar || typeof document === 'undefined') return undefined;
    const html = document.documentElement;
    const root = sidenavRef.current;

    const publish = () => {
      if (isDrawer || !root) {
        html.style.setProperty('--sidenav-current-width', '0px');
        return;
      }
      const styles = window.getComputedStyle(root);
      const occupied =
        root.getBoundingClientRect().width +
        (parseFloat(styles.marginLeft) || 0) +
        (parseFloat(styles.marginRight) || 0);
      html.style.setProperty('--sidenav-current-width', `${Math.round(occupied)}px`);
    };

    publish();
    let observer = null;
    if (root && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(publish);
      observer.observe(root);
    }
    // Al terminar la animación de ancho
    root?.addEventListener('transitionend', publish);

    return () => {
      observer?.disconnect();
      root?.removeEventListener('transitionend', publish);
      html.style.removeProperty('--sidenav-current-width');
    };
  }, [publishWidthVar, isDrawer, state]);

  // Un drawer cerrado queda fuera del orden de tabulación (`inert`). Se pone
  // por propiedad y no por atributo JSX: React 18 y 19 lo tratan distinto.
  useEffect(() => {
    if (sidenavRef.current) sidenavRef.current.inert = isDrawer && !drawerOpen;
  }, [isDrawer, drawerOpen]);

  // "brand": the Trust Navy LSG surface, built on the dark theme's rules and tokens.
  const brand = currentTheme === 'brand';
  const themeClass = brand ? 'sidenav--theme-dark sidenav--theme-brand' : `sidenav--theme-${currentTheme}`;
  const themeAttr = brand ? 'dark' : currentTheme;

  const sidenavClasses = [
    'sidenav',
    `sidenav--${state}`,
    themeClass,
    isDrawer ? 'sidenav--drawer' : '',
    isDrawer && drawerOpen ? 'sidenav--drawer-open' : '',
    config.animations ? 'sidenav--animated' : '',
    indicatedPendingItem ? 'sidenav--navigating' : '',
    className
  ].filter(Boolean).join(' ');

  const rootStyle = {
    ...(width != null && { '--sidenav-width-open': toCssLength(width) }),
    ...(collapsedWidth != null && { '--sidenav-width-collapsed': toCssLength(collapsedWidth) }),
    ...style,
  };

  const toggleLabel = isDrawer
    ? t('toggle.close', 'Close navigation')
    : t('toggle.label', 'Toggle navigation');

  const sidenav = (
    <div
      ref={sidenavRef}
      className={sidenavClasses}
      style={rootStyle}
      role="navigation"
      aria-label={t('navigation.label', 'Main navigation')}
      data-state={state}
      data-theme={themeAttr}
      data-variant={variant}
      aria-busy={indicatedPendingItem ? true : undefined}
      aria-hidden={isDrawer && !drawerOpen ? true : undefined}
      {...props}
    >
      {/* Header con logo */}
      <div className="sidenav-header">
        {renderLogo()}
        <button
          type="button"
          className="sidenav-toggle"
          onClick={toggleState}
          aria-label={toggleLabel}
          title={isDrawer ? toggleLabel : t('toggle.title', 'Toggle navigation state')}
          aria-expanded={isDrawer ? undefined : state === 'open'}
        >
          <svg 
            width="16" 
            height="16" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
            strokeLinecap="round" 
            strokeLinejoin="round"
            aria-hidden="true"
          >
            {isDrawer ? (
              <path d="M18 6L6 18M6 6l12 12" />
            ) : state === 'open' ? (
              <path d="M15 18l-6-6 6-6" />
            ) : (
              <path d="M9 18l6-6-6-6" />
            )}
          </svg>
        </button>
      </div>

      {/* Selector de cuentas (solo si el proyecto activa el flag).
          Va fuera de .sidenav-content a propósito: ese contenedor tiene
          overflow-y auto y recortaría el dropdown inline. */}
      {showAccountSelector && (
        <div className="sidenav-account-block">
          <AccountSelector
            accounts={resolvedAccounts}
            selectedAccountId={selectedAccountId}
            selectedAccount={resolvedSelectedAccount}
            onSelect={onAccountSelect}
            open={accountPanelOpen}
            onOpenChange={setAccountPanelOpen}
            loading={accountsLoading}
            error={accountsError}
            searchable={accountSearchable}
            filters={resolvedAccountFilters}
            selectedFilterId={selectedAccountFilterId}
            onFilterChange={onAccountFilterChange}
            allFilterId={allAccountFilterId}
            searchMode={accountSearchMode}
            onSearchChange={onAccountSearchChange}
            searchDebounceMs={accountSearchDebounceMs}
            fetching={accountsFetching}
            total={accountsTotal}
            sidenavState={state}
            showTriggerBadges={!accountSourceToggleVisible}
            portalClassName={themeClass}
            t={t}
          />
          {accountSourceToggleVisible && (
            <AccountSourceToggle
              sources={accountSourceOptions}
              activeSourceId={selectedAccountSourceId}
              onSourceChange={onAccountSourceChange}
              t={t}
            />
          )}
          <div className="sidenav-account-separator" />
        </div>
      )}

      {/* Navegación principal */}
      <div className="sidenav-content">
        <div className="sidenav-section-title">
          {state === 'open' 
            ? t('navigation.main', 'MAIN NAVIGATION')
            : t('navigation.main.short', 'MAIN')
          }
        </div>
        <nav className="sidenav-nav">
          {renderNavItems(navigationItems)}
        </nav>
      </div>

      {/* Footer opcional */}
      <div className="sidenav-footer">
        <div className="sidenav-items">
          {renderFooterItems()}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {isDrawer
        ? inPortal(
            <>
              {drawerOpen && (
                <div
                  className="sidenav-drawer-backdrop"
                  onClick={() => onOpenChange?.(false)}
                  aria-hidden="true"
                />
              )}
              {sidenav}
            </>
          )
        : sidenav}

      {/* Tooltip para estado collapsed */}
      {tooltipTarget && tooltipContent && state === 'collapsed' && (
        <Tooltip
          target={tooltipTarget}
          content={tooltipContent}
          position={{
            top: tooltipPosition.y,
            left: tooltipPosition.x
          }}
          visible={true}
          placement="right"
        />
      )}
    </>
  );
}

export default Sidenav;